import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import {
	useActivePhoto,
	useSetActivePhoto,
} from '@context/ActivePhotoContext'
import Preview from '@components/display/photos/PreviewDynamic'
import Controls from '@components/display/photos/LightboxControlsDynamic'
import Image from "next/legacy/image"
import ImageWithFallback from '@components/image/WithFallback'
import Inscription from '../offer/Inscription'
import Video from '@components/display/video/Video'
import { ThumbsScrollController } from './ThumbsScrollController'
import fullPhoto from '@utils/links/fullPhoto'
import makeYoutubeThumbnailResolver from '@utils/video/makeYoutubeThumbnailResolver'
import useMediaQueryBreakpoint from '@hooks/useMediaQueryBreakpoint'
import mix from '@utils/styles/mix'

import styles from './MainPhoto.module.css'

const VID_PREFIX = 'vid:'

function Carousel({ alt, fallback, offer, scrollToChild }) {
	const [showFullScreen, setShowFullScreen] = useState(false)
	const mobile = useMediaQueryBreakpoint(800)
	const photoRef = useRef()
	const photosRef = useRef([])
	const thumbsRef = useRef()
	const thumbRefs = useRef([])
	const video = offer?.video_link ? VID_PREFIX + offer.video_link : ''
	const videoThumbnail =
		video && makeYoutubeThumbnailResolver(video)()
	const pid = offer?.tracking?.pid
	const photoOrders = useMemo(() => offer?.photos || [0], [offer])
	const photoLinks = useMemo(() => {
		let links = photoOrders.map((order) => fullPhoto(pid, order))
		return video ? [video].concat(links) : links
	}, [photoOrders, pid, video])

	const currentActivePhoto = useActivePhoto()
	const setActivePhoto = useSetActivePhoto()

	const scrollWrappersToDesiredPhoto = useCallback(
		(id) => {
			scrollToChild(photoRef.current, photosRef.current[id])
			scrollToChild(thumbsRef.current, thumbRefs.current[id])
		},
		[photoRef, scrollToChild, thumbsRef],
	)

	const togglePhoto = useCallback(
		(id) => () => {
			const max = photoLinks.length - 1
			if (id < 0) setActivePhoto(max)
			if (id > max) setActivePhoto(0)
			else setActivePhoto(id)
		},
		[photoLinks, setActivePhoto],
	)

	const showFullScreenPreview = useCallback(() => {
		setShowFullScreen((bool) => !bool)
	}, [])

	const toggleLightbox = useMemo(
		() => (photoLinks ? showFullScreenPreview : undefined),
		[photoLinks, showFullScreenPreview],
	)

	const controlMin = 0
	const controlMax =
		(photoOrders?.length ? photoOrders.length - 1 : controlMin) +
		(video ? 1 : 0)

	const controls = <Controls max={controlMax} min={controlMin} />

	const previewLink = photoLinks[currentActivePhoto] || ''

	useEffect(() => {
		scrollWrappersToDesiredPhoto(currentActivePhoto)
	}, [currentActivePhoto, scrollWrappersToDesiredPhoto])

	return (
		<>
			{showFullScreen && (
				<Preview
					controls={controls}
					count={photoLinks.length}
					lp={currentActivePhoto + 1}
					photo={
						previewLink.startsWith(VID_PREFIX)
							? videoThumbnail
							: previewLink
					}
					toggle={toggleLightbox}
				/>
			)}

			<div className={styles.carousel}>
				<div className={styles.container} ref={photoRef}>
					{photoLinks.map((link, id) => {
						const isVideo = link.startsWith(VID_PREFIX)
						const firstImageIdx = video ? 1 : 0
						const isFirstImage = id === firstImageIdx
						const inscription =
							isFirstImage && offer?.data?.inscription
						const reserved = isFirstImage && offer?.tracking?.reserved

						return (
							<div
								className={mix([
									styles.slide,
									isVideo && styles.video,
								])}
								key={link}
								ref={(el) => (photosRef.current[id] = el)}
							>
								{isVideo ? (
									<Video offer={offer} />
								) : (
									<>
										{(inscription || reserved) && (
											<Inscription
												reserved={reserved}
												text={inscription}
												big
											/>
										)}
										<ImageWithFallback
											alt={alt || ''}
											draggable={false}
											fallback={fallback}
											width={2048}
											height={1362}
											onClick={toggleLightbox}
											priority={isFirstImage}
											src={link}
										/>
									</>
								)}
							</div>
						)
					})}
				</div>

				{controls}
			</div>

			{!mobile && photoLinks.length > 1 && (
				<ThumbsScrollController forwardRef={thumbsRef}>
					<div className={styles.thumbnails} ref={thumbsRef}>
						{photoLinks.map((link, id) => {
							const isVideo = link.startsWith(VID_PREFIX)
							const src = isVideo
								? makeYoutubeThumbnailResolver(
										link.slice(VID_PREFIX.length),
								  )()
								: `${link}?ffoxfix=1`

							return (
								<span
									key={link}
									onClick={togglePhoto(id)}
									ref={(el) => (thumbRefs.current[id] = el)}
								>
									<Image
										alt={alt || ''}
										draggable={false}
										fallback={fallback}
										height={92}
										src={src}
										width={140}
									/>
								</span>
							)
						})}
					</div>
				</ThumbsScrollController>
			)}
		</>
	)
}

export default Carousel